import { API } from '@/modules/api-service/api-service';
import type { Core, Data } from '@strapi/types';

const url = '/project-documents';

export const getDocumentsList = async ({
	projectId,
	statusId,
}: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'getAllProjectDocuments'>) => {
	const { data: response } = await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'getAllProjectDocuments'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'getAllProjectDocuments'>
	>(url, { projectId, statusId });

	return response;
};

export const getFilteredDocumentsList = async ({
	projectId,
	date,
	documentType,
	projectDocumentId,
}: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'getFilteredDocumentsList'>) => {
	const { data: response } = await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'getFilteredDocumentsList'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'getFilteredDocumentsList'>
	>(`${url}/filter`, { projectId, date, documentType, projectDocumentId });

	return response;
};

export const openPreviousVersionApi = async (
	body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'openPreviousVersion'>,
) => {
	const { documentFileId, projectDocumentId } = body;

	return await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'openPreviousVersion'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'openPreviousVersion'>
	>(`${url}/open-previous-version`, { documentFileId, projectDocumentId });
};

export const duplicateProjectDocumentApi = async ({
	documentId,
}: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'duplicateProjectDocument'>) => {
	return await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'duplicateProjectDocument'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'duplicateProjectDocument'>
	>(`${url}/duplicate`, { documentId });
};

export const moveProjectDocumentApi = async ({
	documentId,
	body,
}: {
	documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
	body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'moveToProject'>;
}) => {
	return await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'moveToProject'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'moveToProject'>
	>(`${url}/move-to-project/${documentId}`, body);
};

export const createProjectDocumentApi = async ({
	documentId,
	body,
}: {
	documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
	body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'create'>;
}) => {
	return await API().post<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'create'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'create'>
	>(`${url}/${documentId}`, body);
};

export const updateProjectDocumentApi = async ({
	projectDocumentId,
	body,
}: {
	projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
	body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'update'>;
}) => {
	return await API().put<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'update'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'update'>
	>(`${url}/${projectDocumentId}`, body);
};

export const updateProjectDocumentStatusApi = async ({
	projectDocumentId,
	body,
}: {
	projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
	body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'updateStatus'>;
}) => {
	return await API().put<
		Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'updateStatus'>,
		Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'updateStatus'>
	>(`${url}/status/${projectDocumentId}`, body);
};

export const deleteProjectDocumentApi = async ({
	projectDocumentId,
}: {
	projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
}) => {
	return await API().delete(`${url}/${projectDocumentId}`);
};

export const downloadProjectDocumentApi = async (
	projectDocumentId: Pick<Data.Entity<'api::project-document.project-document'>, 'documentId'>['documentId'],
) => {
	return await API().get<Core.Helpers.Services.GetReturnType<'api::document-file.document-file', 'getLatestDownloadUrl'>>(
		`/document-files/download-file/${projectDocumentId}`,
	);
};

export const uploadProjectDocumentFileApi = async ({
	projectId,
	body,
}: {
	projectId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
	body: FormData;
}) => {
	return await API().post<Core.Helpers.Services.GetReturnType<'api::document-file.document-file', 'uploadDocumentFile'>, FormData>(
		`/document-files/upload/${projectId}`,
		body,
	);
};
